import { Alert, Box, Button, DialogActions, Tab, Tabs, Typography } from '@mui/material';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { BasicDialog } from '../../../components/Dialog/BasicDialog';
import { ConfirmDialog } from '../../../components/Dialog/ConfirmDialog/ConfirmDialog';
import { FileIngestionConfig, IngestionConfigSchema } from '../../../services/queries/FileIngestionQueries';
import { DATA_TYPE_LABELS, dataTypeHasPeriod, deriveDataType, reconcileDataType } from './data-type';
import {
  useCreateFieldAutomationConfig,
  useUpdateFieldAutomationConfig,
} from './state/field-automation-state';
import { BasicInfoSection } from './sections/BasicInfoSection';
import { IngestionSettingsSection } from './sections/FileSettingsSection';
import { FieldMappingsSection } from './sections/FieldMappingsSection';
import { SnowflakeConfigSection } from './sections/SnowflakeConfigSection';

interface FormValues {
  fileType: string;
  displayName: string;
  description: string;
  active: boolean;
  config: IngestionConfigSchema;
}

interface FieldAutomationConfigFormProps {
  config: FileIngestionConfig | null;
  onClose: () => void;
}

export function FieldAutomationConfigForm({ config, onClose }: FieldAutomationConfigFormProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const isEditMode = !!config;

  const { mutate: createConfig, isPending: isCreating } = useCreateFieldAutomationConfig();
  const { mutate: updateConfig, isPending: isUpdating } = useUpdateFieldAutomationConfig();

  const methods = useForm<FormValues>({
    defaultValues: config
      ? {
          fileType: config.fileType,
          displayName: config.displayName,
          description: config.description || '',
          active: config.active,
          config: (() => {
            const base = {
              ...config.config,
              sourceType: config.config.sourceType || 'file',
              fileLevel: {
                ...config.config.fileLevel,
                delimiter: config.config.fileLevel?.delimiter || ',',
                // Default rowStructure for Snowflake configs that pre-date this field
                rowStructure:
                  config.config.fileLevel?.rowStructure ??
                  (config.config.sourceType === 'snowflake' ? 'row-per-entity-period' : undefined),
              },
              // Promote integrationSync.* fields to top-level if not already set (backward compat)
              periodLengthMappings:
                config.config.periodLengthMappings ??
                config.config.integrationSync?.periodLengthMappings ??
                [],
              scenarioMappings:
                config.config.scenarioMappings ?? config.config.integrationSync?.scenarioMappings ?? [],
              upsertStrategy: config.config.upsertStrategy ?? config.config.integrationSync?.upsertStrategy,
              budgetId: config.config.budgetId ?? config.config.integrationSync?.budgetId,
            };

            // Backward compat for Snowflake configs saved with the old matchKeyResolver /
            // matchKeyColumn fields: lift them into entityMapping so they line up with
            // the unified Ingestion Settings UI shared with file ingestion.
            if (config.config.sourceType === 'snowflake') {
              const sfLegacy = config.config.snowflakeConfig as Record<string, unknown> | undefined;
              const existingEm = config.config.entityMapping ?? {};
              const needsLookupMigration = !existingEm.lookupStrategy && sfLegacy?.matchKeyResolver;
              const needsIdentifierMigration = !existingEm.identifierField && sfLegacy?.matchKeyColumn;
              if (needsLookupMigration || needsIdentifierMigration) {
                base.entityMapping = {
                  targetEntity: 'company',
                  ...existingEm,
                  ...(needsLookupMigration
                    ? {
                        lookupStrategy:
                          sfLegacy?.matchKeyResolver === 'companyProvider'
                            ? 'provider-lookup'
                            : sfLegacy?.matchKeyResolver === 'companyId'
                              ? 'id-lookup'
                              : 'name-lookup',
                        providerName:
                          existingEm.providerName ??
                          (sfLegacy?.matchKeyResolverProvider as string | undefined) ??
                          '',
                      }
                    : {}),
                  ...(needsIdentifierMigration
                    ? { identifierField: sfLegacy?.matchKeyColumn as string }
                    : {}),
                };
              }
            }
            // Data Type (FD-3697): derive from the massaged config (base has the legacy
            // rowStructure defaults applied) so pre-dataType configs open correctly, and
            // clamp to what the source can run so the select never gets an off-list value.
            return {
              ...base,
              dataType: reconcileDataType(base.sourceType, deriveDataType(base as IngestionConfigSchema)),
            };
          })(),
        }
      : {
          fileType: '',
          displayName: '',
          description: '',
          active: true,
          config: {
            sourceType: 'file',
            dataType: 'entity-properties',
            detection: {
              filenamePatterns: [],
              s3PathPatterns: [],
            },
            fileLevel: {
              sourceFormat: 'csv',
              delimiter: ',',
              rowStructure: 'row-per-entity',
              kpiMetadata: {
                dateColumn: '',
                periodTypeColumn: '',
                sectionColumn: '',
              },
            },
            entityMapping: {
              targetEntity: 'company',
              identifierField: '',
              lookupStrategy: 'name-lookup',
              providerName: '',
              dateField: '',
              createIfNotFound: true,
              dateTolerance: 0,
              fundIdentifierField: '',
            },
            fieldMappings: [],
            periodLengthMappings: [],
            scenarioMappings: [],
            integrationSync: {
              metricMappings: [],
            },
          },
        },
  });

  const {
    handleSubmit,
    formState: { isDirty },
    watch,
  } = methods;

  const sourceType = watch('config.sourceType') || 'file';
  const isApiIntegration = sourceType === 'api-integration';
  const isSnowflake = sourceType === 'snowflake';

  const fileType = watch('fileType');
  const displayName = watch('displayName');
  const integrationProvider = watch('config.integrationProvider');
  const fieldMappings = watch('config.fieldMappings');
  const metricMappings = watch('config.integrationSync.metricMappings');
  const kpiMetadata = watch('config.fileLevel.kpiMetadata');
  const targetEntity = watch('config.entityMapping.targetEntity');
  const dateField = watch('config.entityMapping.dateField');
  const snowflakeConfig = watch('config.snowflakeConfig');
  const entityMapping = watch('config.entityMapping');

  // Data Type (FD-3697) drives period requirements; "has a period?" is the axis.
  const dataType = watch('config.dataType') ?? 'entity-properties';
  const isFinancials = dataType === 'company-financials';
  const hasPeriod = dataTypeHasPeriod(dataType);

  // Per-mapping shape check shared by Snowflake and file modes. API-integration
  // configs skip this entirely (mappings auto-create from staging).
  const isCompleteMapping = (mapping: {
    sourceColumn?: string | string[];
    targetType?: string;
    entityField?: string;
    kpiId?: number;
  }) => {
    const hasSource =
      mapping.sourceColumn &&
      (typeof mapping.sourceColumn === 'string'
        ? mapping.sourceColumn.trim()
        : Array.isArray(mapping.sourceColumn) && mapping.sourceColumn.some((col) => col && col.trim()));
    const hasTarget =
      mapping.targetType &&
      ((mapping.targetType === 'kpi' && mapping.kpiId) ||
        ((mapping.targetType === 'entity-field' || mapping.targetType === 'custom-field') &&
          mapping.entityField) ||
        // commentary maps to (entity, reporting period) — no destination id needed
        mapping.targetType === 'commentary');
    return Boolean(hasSource && hasTarget);
  };

  // Every unmet requirement is listed here verbatim (shown in an Alert above the
  // buttons), so a disabled Update button is never silent. Empty ⇒ the form is valid.
  const validationIssues: string[] = [];

  if (!fileType) {
    validationIssues.push(sourceType === 'file' ? 'File Type is required.' : 'Identifier is required.');
  }
  if (!displayName) {
    validationIssues.push('Display Name is required.');
  }

  if (isApiIntegration) {
    if (!integrationProvider) {
      validationIssues.push('Select an Integration Provider.');
    }
    // Same grandfather rule as the file/snowflake branches: KPI-targeting metric mappings
    // are only coherent on a periodic config. There is deliberately no "financials needs
    // at least one metric mapping" rule — legacy auto-create configs legitimately have none.
    if (!hasPeriod && metricMappings?.some((m) => m.targetType === 'kpi')) {
      validationIssues.push(
        `${DATA_TYPE_LABELS['entity-properties']} configs cannot keep KPI metric mappings — retarget or delete them, or switch the Data Type.`
      );
    }
  } else {
    if (!fieldMappings || fieldMappings.length === 0) {
      validationIssues.push('Add at least one field mapping.');
    } else {
      if (!fieldMappings.every(isCompleteMapping)) {
        validationIssues.push('Complete all field mappings — each needs a source column and a target.');
      }
      // The mapping set must agree with the Data Type: KPI/commentary writes are keyed
      // on (entity, reporting period), which only exists in periodic configs — saving a
      // mismatch either no-ops or, worse, dates every row with the sync run date.
      // Deliberate asymmetry: off-type mappings ARE allowed under the periodic types
      // (e.g. entity-field alongside kpi under Company Financials) — mixed legacy configs
      // are grandfathered and both target kinds carry a period, so there is no corruption
      // risk; only the no-period case (entity properties) must exclude them.
      if (isFinancials && !fieldMappings.some((m) => m.targetType === 'kpi')) {
        validationIssues.push('Company Financials requires at least one mapping that targets a KPI.');
      }
      if (dataType === 'commentary' && !fieldMappings.some((m) => m.targetType === 'commentary')) {
        validationIssues.push('Performance Commentary requires at least one commentary mapping.');
      }
      if (!hasPeriod && fieldMappings.some((m) => m.targetType === 'kpi' || m.targetType === 'commentary')) {
        validationIssues.push(
          `${DATA_TYPE_LABELS['entity-properties']} configs cannot keep KPI or commentary mappings — retarget or delete them, or switch the Data Type.`
        );
      }
    }

    if (isSnowflake) {
      if (!snowflakeConfig?.connectionId) {
        validationIssues.push('Select a Snowflake connection.');
      }
      if (!snowflakeConfig?.sourceTable) {
        validationIssues.push('Select a source table or view.');
      }
      if (!entityMapping?.identifierField) {
        validationIssues.push('Set the Company Identifier Column.');
      }
      if (!entityMapping?.lookupStrategy) {
        validationIssues.push('Select a Company Lookup Strategy.');
      }
      if (hasPeriod && !snowflakeConfig?.reportingPeriodColumn) {
        validationIssues.push('Set the Reporting Period Column.');
      }
    } else {
      if (hasPeriod && !kpiMetadata?.dateColumn?.trim()) {
        validationIssues.push('Set the Date Column in Date & Period.');
      }
    }

    if (entityMapping?.lookupStrategy === 'provider-lookup' && !entityMapping.providerName) {
      validationIssues.push('Set the Provider Name for Provider Lookup.');
    }
    if (targetEntity === 'round' && !(typeof dateField === 'string' && dateField.trim())) {
      validationIssues.push('Round mappings require a Date Field.');
    }
  }

  const isValid = validationIssues.length === 0;

  const onSubmit = (data: FormValues) => {
    // Re-validate before submitting to prevent race conditions
    if (!isValid) {
      console.warn('Form submitted with invalid state, blocking submission');
      return;
    }

    // Data-type → legacy-field write-through happens in the create/update mutation
    // hooks (the save chokepoint), so every caller persists consistent fields.
    if (isEditMode && config) {
      updateConfig(
        {
          id: config.id,
          payload: {
            displayName: data.displayName,
            description: data.description,
            active: data.active,
            config: data.config,
          },
        },
        {
          onSuccess: () => {
            onClose();
          },
        }
      );
    } else {
      createConfig(
        {
          fileType: data.fileType,
          displayName: data.displayName,
          description: data.description,
          active: data.active,
          config: data.config,
        },
        {
          onSuccess: () => {
            onClose();
          },
        }
      );
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowCloseConfirm(false);
    onClose();
  };

  const tabs = ['Basic Info', 'Ingestion Settings', 'Field Mappings'];

  return (
    <BasicDialog
      open={true}
      onClose={handleClose}
      title={isEditMode ? `Edit Configuration: ${config.displayName}` : 'Create New Configuration'}
      maxWidth='lg'
      fullWidth={true}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', paddingX: '2.5rem' }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              {tabs.map((label) => (
                <Tab key={label} label={label} />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ padding: '2rem 2.5rem' }}>
            {activeTab === 0 && <BasicInfoSection isEditMode={isEditMode} />}
            {/* Tab 2: Snowflake shows its connection/table/column picker followed by the shared
                ingestion settings (entity mapping + row structure). Other source types show only
                the ingestion settings (file format, detection, entity mapping, etc.). */}
            {activeTab === 1 && (
              <>
                {isSnowflake && <SnowflakeConfigSection />}
                <Box sx={isSnowflake ? { mt: 6 } : undefined}>
                  <IngestionSettingsSection />
                </Box>
              </>
            )}
            {activeTab === 2 && <FieldMappingsSection />}
          </Box>

          {isDirty && validationIssues.length > 0 && (
            <Alert severity='info' sx={{ marginX: '2.5rem', marginBottom: '0.5rem' }}>
              <Typography variant='body2' fontWeight={600} gutterBottom>
                To save this configuration:
              </Typography>
              {validationIssues.map((issue) => (
                <Typography key={issue} variant='body2'>
                  • {issue}
                </Typography>
              ))}
            </Alert>
          )}
          <DialogActions sx={{ paddingX: '2.5rem', paddingBottom: '2rem' }}>
            <Button onClick={handleClose} color='secondary' variant='outlined'>
              Cancel
            </Button>
            <Button
              type='submit'
              color='secondary'
              variant='contained'
              disabled={!isDirty || !isValid || isCreating || isUpdating}
            >
              {isEditMode ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
      <ConfirmDialog
        open={showCloseConfirm}
        title='Discard Changes?'
        onClose={() => setShowCloseConfirm(false)}
        onConfirm={handleConfirmClose}
        footerButtonsProps={{
          confirmButtonText: 'Discard',
          cancelButtonText: 'Keep Editing',
        }}
      >
        <Typography>You have unsaved changes. Are you sure you want to close?</Typography>
      </ConfirmDialog>
    </BasicDialog>
  );
}
